import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { AiFillSetting } from 'react-icons/ai';
import { LineChart, XAxis, YAxis, Tooltip, ResponsiveContainer, Line } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { athleteAPI, trainingSessionAPI, authAPI, bananaCupAPI } from '../../services/api';
import { Athlete, GoalPeriod } from '../../types/mongodb';
import { PageTitle } from '../CoachDashboard/styles';
import LoadingSpinner from '../Common/LoadingSpinner';
import PlanBadge from '../Common/PlanBadge';
import JumperDashboardSettings from './JumperDashboardSettings';

interface TrainingSession {
  _id: string;
  trainingType: 'ski_jumping' | 'ground_training' | 'cross_country' | 'competition';
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  weather?: string;
  category?: string;
  hillSize?: string;
  athleteIds: Array<{ _id: string; firstName: string; lastName: string }>;
  jumps?: Array<{
    _id?: string;
    athleteId: { _id: string; firstName: string; lastName: string };
    distance?: number;
    startingGate?: number;
    wind?: string;
    speed?: number;
    telemark?: boolean;
    hillSize?: string;
    suitId?: any;
    skisId?: any;
    shoesId?: any;
    rating?: number;
  }>;
  competitionJumps?: Array<{
    _id?: string;
    athleteId: { _id: string; firstName: string; lastName: string };
    trialRoundStartingGate?: number;
    trialRoundDistance?: number;
    trialRoundWind?: string;
    trialRoundSpeed?: number;
    trialRoundTelemark?: boolean;
    firstRoundStartingGate?: number;
    firstRoundDistance?: number;
    firstRoundWind?: string;
    firstRoundSpeed?: number;
    firstRoundTelemark?: boolean;
    secondRoundStartingGate?: number;
    secondRoundDistance?: number;
    secondRoundWind?: string;
    secondRoundSpeed?: number;
    secondRoundTelemark?: boolean;
    thirdRoundStartingGate?: number;
    thirdRoundDistance?: number;
    thirdRoundWind?: string;
    thirdRoundSpeed?: number;
    thirdRoundTelemark?: boolean;
    fourthRoundStartingGate?: number;
    fourthRoundDistance?: number;
    fourthRoundWind?: string;
    fourthRoundSpeed?: number;
    fourthRoundTelemark?: boolean;
    fifthRoundStartingGate?: number;
    fifthRoundDistance?: number;
    fifthRoundWind?: string;
    fifthRoundSpeed?: number;
    fifthRoundTelemark?: boolean;
    hillSize?: string;
    result?: number;
    suitId?: any;
    skisId?: any;
    shoesId?: any;
  }>;
  groundTrainings?: Array<{
    athleteId: { _id: string; firstName: string; lastName: string } | string;
    trainingType: string;
    intensity: number;
  }>;
}

const Container = styled.div`
  /* Spacing above "Your dashboard": first value is padding-top (reduce to bring title higher) */
  padding: -3.0rem 1.25rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  box-sizing: border-box;
  @media (max-width: 768px) {
    overflow-x: hidden;
    /* Mobile: first value = padding-top above content */
    padding: 0.25rem 0.75rem 1.5rem;
  }
`;

/** Inline icon for settings (matches coach home) */
const Icon = ({ component: Component, ...props }: { component: any; [key: string]: any }) => {
  return Component ? <Component {...props} /> : null;
};

/** Single header row: title + badge on left, 3 buttons on right — aligned to top so buttons match title height */
const SettingsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  margin-top: 0;
  margin-bottom: 0.25rem;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    margin-bottom: 0.25rem;
    gap: 0.5rem;
    flex-wrap: nowrap;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  flex-shrink: 0;
  @media (max-width: 768px) {
    min-width: 0;
    flex: 1 1 auto;
    max-width: calc(100% - 88px);
  }
`;

const TitleAndBadgeRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

const TopRightGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1.25rem;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-end;
    gap: 0.35rem;
    flex-shrink: 0;
  }
`;

const AddFabContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  @media (max-width: 768px) {
    display: none;
  }
`;

const AddFabButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: var(--accent-orange);
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 700;
  transition: transform 0.2s ease, background 0.2s;
  box-shadow: 0 2px 8px rgba(255, 150, 0, 0.3);
  &:hover {
    background: var(--accent-orange-dark);
    transform: scale(1.05);
  }
`;

const AddLabel = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--text-gray);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
`;

const AddFabMenu = styled.div`
  position: absolute;
  top: 0;
  right: 100%;
  margin-right: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 180px;
  z-index: 99;
  animation: fadeIn 0.2s ease;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateX(4px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @media (max-width: 768px) {
    top: 100%;
    right: 0;
    left: auto;
    margin-right: 0;
    margin-top: 0.35rem;
    animation: fadeInMobile 0.2s ease;
  }
  @keyframes fadeInMobile {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const AddFabOption = styled.button`
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  background: var(--accent-orange);
  color: #fff;
  border: none;
  font-family: 'Poppins', sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  &:hover {
    background: var(--accent-orange-dark);
  }
`;

const SettingsButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: transparent;
  color: var(--accent-orange);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  transition: all 0.2s;
  &:hover {
    color: var(--accent-orange-dark);
    transform: scale(1.1);
  }
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 1.1rem;
  }
`;

const ChooseMetricsLabel = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--text-gray);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: pre-line;
  text-align: center;
  line-height: 1.2;
`;

const MetricsButtonColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
`;

const HelpButtonAsLink = styled(Link)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: transparent;
  color: var(--accent-orange);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.2s;
  &:hover {
    color: var(--accent-orange-dark);
    transform: scale(1.1);
  }
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 1.2rem;
  }
`;

const HelpButtonColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
`;

const HelpLabel = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--text-gray);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
`;

const PlanBadgeWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const GraphSection = styled.div`
  width: 100%;
  max-width: 1400px;
  background: var(--bg-sidebar);
  border-radius: 16px;
  padding: 1.5rem 1.5rem 1.5rem 0.5rem;
  margin-bottom: 3rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  @media (max-width: 768px) {
    margin-left: -0.75rem;
    margin-right: -0.75rem;
    width: calc(100% + 1.5rem);
    padding: 1rem 0.75rem 1rem 0.75rem;
    border-radius: 0;
  }
`;

const GraphTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 800;
  color: var(--text-gray);
  margin-bottom: 0.75rem;
  text-align: center;
`;

const GraphContainer = styled.div`
  width: 100%;
  height: 260px;
  .recharts-cartesian-grid-horizontal line,
  .recharts-cartesian-grid-vertical line {
    stroke: rgba(0,0,0,0.06);
  }
  .recharts-text {
    font-size: 0.7rem;
    fill: var(--text-gray);
  }
  .recharts-tooltip-wrapper {
    outline: none;
  }
  .recharts-legend-item-text {
    font-size: 0.65rem;
    font-weight: 600;
  }
  @media (max-width: 768px) {
    .recharts-legend-wrapper {
      margin-top: 1.5rem;
      padding-top: 0.35rem;
    }
    .recharts-legend-item {
      margin-bottom: 0.15rem;
      margin-top: 0;
    }
  }
`;

const TwoGraphsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  width: 100%;
  max-width: 1400px;
  margin-bottom: 3rem;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-left: -0.75rem;
    margin-right: -0.75rem;
    width: calc(100% + 1.5rem);
    padding: 0;
  }
`;

const SmallGraphSection = styled.div`
  background: var(--bg-sidebar);
  border-radius: 16px;
  padding: 1.5rem 1rem 1.5rem 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    padding: 1rem 1rem 1rem 0;
    border-radius: 0;
  }
`;

const SmallGraphContainer = styled.div`
  width: 100%;
  height: 220px;
  .recharts-cartesian-grid-horizontal line,
  .recharts-cartesian-grid-vertical line {
    stroke: rgba(0,0,0,0.06);
  }
  .recharts-text {
    font-size: 0.65rem;
    fill: var(--text-gray);
  }
  .recharts-tooltip-wrapper {
    outline: none;
  }
  @media (max-width: 768px) {
    height: 200px;
  }
`;

const SessionListSection = styled.div`
  @media (max-width: 768px) {
    margin-left: -2rem;
    padding-left: 0.5rem;
    width: calc(100% + 2rem);
    box-sizing: border-box;
  }
`;

const WelcomeMsg = styled.div`
  font-size: 1rem;
  font-weight: 400;
  margin-top: 0.2rem;
  margin-bottom: 2.5rem;
  text-align: left;
  max-width: 700px;
  color: var(--text-gray);

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-top: 0.15rem;
    margin-bottom: 1.5rem;
    padding-right: 0;
    max-width: 100%;
  }
`;

const Sj = styled.span`
  color: var(--accent-orange);
  font-weight: 700;
`;

const Mate = styled.span`
  color: var(--text-dark);
  font-weight: 700;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  width: 100%;
  max-width: 1400px;
  margin-bottom: 3rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const MetricCard = styled.div`
  background: var(--bg-sidebar);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s, transform 0.2s;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 1.2rem;
  }
`;

const MetricLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-gray);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
  opacity: 0.7;
`;

const MetricValue = styled.div`
  font-size: 2rem;
  font-weight: 900;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const MetricDescription = styled.div`
  font-size: 0.85rem;
  color: var(--text-gray);
  opacity: 0.7;
  margin-top: 0.25rem;
`;

const InfoSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: calc(var(--spacing-xl) * 1.5);
  width: 100%;
  max-width: 1400px;
`;

const InfoCard = styled.div`
  background: var(--bg-sidebar);
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
`;

const InfoLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  margin-bottom: 0.3rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InfoValue = styled.div`
  font-size: 1.2rem;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-weight: 900;
`;

const LinkAthleteCard = styled.div`
  background: var(--bg-sidebar);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 1400px;
  margin-bottom: 2rem;
`;

const LinkAthleteTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 900;
  color: var(--accent-orange);
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
`;

const LinkAthleteInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e0e6ed;
  border-radius: var(--radius-md);
  font-size: 1rem;
  background: #f5f5f5;
  color: var(--text-gray);
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;

  &:focus {
    outline: none;
    border-color: var(--accent-orange);
    background: var(--bg-primary);
  }
`;

const LinkButton = styled.button`
  background: var(--accent-orange);
  color: #fff;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
  font-family: 'Poppins', sans-serif;

  &:hover {
    background: var(--accent-orange-dark);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: var(--radius-md);
  padding: 1rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  font-weight: 500;
`;

const SuccessMessage = styled.div`
  color: #4caf50;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: var(--radius-md);
  padding: 1rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  font-weight: 500;
`;

const AnimatedDropdownContainer = styled.div<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '5000px' : '0'};
  overflow: hidden;
  transition: max-height 0.4s ease-in-out, opacity 0.3s ease-in-out, margin-top 0.3s ease-in-out;
  opacity: ${props => props.isOpen ? '1' : '0'};
  margin-top: ${props => props.isOpen ? '0' : '-1rem'};
  width: 100%;
  padding: ${props => props.isOpen ? '0.5rem 0' : '0'};
`;

const SectionTitle = styled.h2`
  color: var(--accent-orange);
  font-size: 0.95rem;
  font-weight: 900;
  font-family: 'Poppins', sans-serif;
  margin-bottom: 1.2rem;
  margin-top: 0.5rem;
  position: relative;
  padding-left: 0.8rem;
  letter-spacing: -0.02em;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 4px;
    height: 100%;
    background: var(--accent-orange);
  }
`;

const TrainingTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 0.3rem;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 0.7rem 0.5rem 0.7rem 0;
  color: var(--text-gray);
  font-size: 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TableRow = styled.tr`
  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

const TableCell = styled.td`
  padding: 0.6rem 0.5rem 0.6rem 0;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const TypeBadge = styled.span<{ type: string }>`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${props => {
    switch (props.type) {
      case 'ski_jumping': return 'rgba(33, 150, 243, 0.15)';
      case 'competition': return 'rgba(255, 87, 34, 0.1)';
      case 'ground_training': return 'rgba(76, 175, 80, 0.1)';
      case 'cross_country': return 'rgba(33, 150, 243, 0.1)';
      case 'banana_cup': return 'rgba(255, 235, 59, 0.15)';
      default: return 'rgba(0, 0, 0, 0.05)';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'ski_jumping': return '#1976D2';
      case 'competition': return '#FF5722';
      case 'ground_training': return '#4CAF50';
      case 'cross_country': return '#2196F3';
      case 'banana_cup': return '#F9A825';
      default: return 'var(--text-gray)';
    }
  }};
`;

interface SessionItem {
  _id: string;
  type: 'training' | 'banana_cup';
  trainingType?: 'ski_jumping' | 'ground_training' | 'cross_country' | 'competition' | 'banana_cup';
  date: string;
  location: string;
  details: string;
  bananaCupName?: string;
  competitionNumber?: number;
}

const ActionButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
  align-items: stretch;

  @media (min-width: 769px) {
    flex-direction: row;
    gap: 1rem;
  }
`;

const ActionButtonWrapper = styled.div`
  flex: 1;
  min-width: 0;

  @media (min-width: 769px) {
    flex: 1;
  }
`;

const DropdownsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const ActionButton = styled.button<{ isExpanded?: boolean; showArrow?: boolean }>`
  background: ${props => props.isExpanded ? 'var(--accent-orange-dark)' : 'var(--accent-orange)'};
  color: #fff;
  border: 2px solid ${props => props.isExpanded ? 'var(--accent-orange)' : 'var(--accent-orange)'};
  border-radius: 0.5rem;
  padding: 0.7rem 1.5rem;
  font-size: 0.85rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Poppins', sans-serif;
  box-shadow: none;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;

  @media (min-width: 769px) {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    width: 100%;
  }

  &::after {
    content: ${props => props.showArrow !== false ? "'▼'" : "''"};
    font-size: 0.7rem;
    transition: transform 0.3s ease;
    transform: ${props => props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  }

  &:hover {
    background: var(--accent-orange-dark);
    border-color: var(--accent-orange-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: var(--spacing-md);
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: var(--bg-primary);
  padding: 2.2rem 2.2rem 1.7rem 2.2rem;
  border-radius: var(--radius-lg);
  width: 90%;
  max-width: 420px;
  border: none;
  box-shadow: var(--shadow-lg);
  animation: modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  h2 {
    font-size: 1.4rem;
    margin: 0 0 1.5rem 0;
    font-family: 'Poppins', sans-serif;
    color: var(--accent-orange);
    font-weight: 900;
    text-align: left;
    letter-spacing: -0.02em;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  position: relative;
  z-index: 1;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  position: relative;
`;

const Label = styled.label`
  font-weight: 700;
  color: var(--text-gray);
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  margin-bottom: 0.1rem;
  font-family: 'Poppins', sans-serif;
`;

const Input = styled.input`
  padding: 0.7rem 1rem;
  background: #f5f5f5 !important;
  border: 1.5px solid #e0e6ed !important;
  border-radius: var(--radius-md);
  font-size: 1rem;
  color: var(--text-gray) !important;
  font-family: 'Poppins', sans-serif;
  transition: border 0.2s, background 0.2s;
  box-shadow: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &:focus {
    outline: none;
    border-color: var(--accent-orange) !important;
    background: #fff !important;
  }

  &::placeholder {
    color: var(--text-muted) !important;
  }

  &[type="date"] {
    background: #f5f5f5 !important;
    color: var(--text-gray) !important;
    
    &::-webkit-calendar-picker-indicator {
      filter: none;
      cursor: pointer;
      opacity: 0.6;
    }
    
    &:focus {
      background: #fff !important;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.2rem;
`;

const ModalButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.4rem 1rem;
  border-radius: 0.5rem;
  border: ${props => props.variant === 'primary' ? '2px solid var(--accent-orange)' : '2px solid #e0e6ed'};
  background: ${props => props.variant === 'primary' ? 'var(--accent-orange)' : 'transparent'};
  color: ${props => props.variant === 'primary' ? '#fff' : 'var(--text-gray)'};
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  cursor: pointer;
  box-shadow: none;
  transition: all 0.2s ease;
  min-width: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  &:hover {
    background: ${props => props.variant === 'primary' ? 'var(--accent-orange-dark)' : 'rgba(0, 0, 0, 0.05)'};
    border-color: ${props => props.variant === 'primary' ? 'var(--accent-orange-dark)' : '#e0e6ed'};
    transform: ${props => props.variant === 'primary' ? 'translateY(-1px)' : 'none'};
    box-shadow: ${props => props.variant === 'primary' ? '0 4px 8px rgba(0, 0, 0, 0.1)' : 'none'};
  }

  &:active {
    transform: ${props => props.variant === 'primary' ? 'translateY(0)' : 'none'};
  }
`;

const WeightHistoryTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 1rem;
`;

const WeightHistoryHeader = styled.th`
  text-align: left;
  padding: 0.7rem 0.5rem 0.7rem 0;
  color: var(--text-gray);
  font-size: 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const WeightHistoryRow = styled.tr`
  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

const WeightHistoryCell = styled.td`
  padding: 0.6rem 0.5rem 0.6rem 0;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const HeightHistoryTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 1rem;
`;

const HeightHistoryHeader = styled.th`
  text-align: left;
  padding: 0.7rem 0.5rem 0.7rem 0;
  color: var(--text-gray);
  font-size: 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const HeightHistoryRow = styled.tr`
  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

const HeightHistoryCell = styled.td`
  padding: 0.6rem 0.5rem 0.6rem 0;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const DEFAULT_JUMPER_TOP_METRICS = ['totalJumps', 'averageDistance', 'bestDistance', 'totalSessions'];

const JumperHome: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshProfile } = useAuth();
  const [athlete, setAthlete] = useState<Athlete | null>(null);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [bananaCups, setBananaCups] = useState<any[]>([]);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalJumps: 0,
    averageDistance: 0,
    bestDistance: 0,
    totalSessions: 0,
    totalWins: 0,
    totalJumpsThisMonth: 0,
    averageDistanceThisMonth: 0,
    bestDistanceThisMonth: 0,
    totalSessionsThisMonth: 0,
    totalWinsThisMonth: 0,
  });
  const [athleteIdInput, setAthleteIdInput] = useState('');
  const [linkError, setLinkError] = useState('');
  const [linkSuccess, setLinkSuccess] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [topMetrics, setTopMetrics] = useState<string[]>(
    (user?.dashboardPreferences as { topMetrics?: string[] } | undefined)?.topMetrics || DEFAULT_JUMPER_TOP_METRICS
  );
  const [addFabExpanded, setAddFabExpanded] = useState(false);
  const addFabContainerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    if (!addFabExpanded) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (addFabContainerRef.current && !addFabContainerRef.current.contains(e.target as Node)) {
        setAddFabExpanded(false);
      }
    };
    const handleScroll = () => setAddFabExpanded(false);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [addFabExpanded]);

  useEffect(() => {
    const prefs = (user?.dashboardPreferences as { topMetrics?: string[] } | undefined)?.topMetrics;
    if (prefs && Array.isArray(prefs) && prefs.length >= 4) {
      setTopMetrics(prefs);
    } else if (prefs && Array.isArray(prefs) && prefs.length > 0) {
      setTopMetrics((prev) => {
        const merged = [...prefs];
        const needed = 4 - merged.length;
        const defaults = DEFAULT_JUMPER_TOP_METRICS.filter((m) => !merged.includes(m));
        for (let i = 0; i < needed && i < defaults.length; i++) merged.push(defaults[i]);
        return merged.slice(0, 4);
      });
    } else {
      setTopMetrics(DEFAULT_JUMPER_TOP_METRICS);
    }
  }, [user?.dashboardPreferences]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Weight, Height, Goals, Trophy Cabinet – single open section (accordion)
  const [openSection, setOpenSection] = useState<'weight' | 'height' | 'goals' | 'trophy' | null>(null);
  const [isWeightModalOpen, setIsWeightModalOpen] = useState(false);
  const [weightFormData, setWeightFormData] = useState({ weight: '', date: '' });
  const [editingWeightEntryId, setEditingWeightEntryId] = useState<string | null>(null);
  const [isHeightModalOpen, setIsHeightModalOpen] = useState(false);
  const [heightFormData, setHeightFormData] = useState({ height: '', date: '' });
  const [editingHeightEntryId, setEditingHeightEntryId] = useState<string | null>(null);
  const [isNewGoalModalOpen, setIsNewGoalModalOpen] = useState(false);
  const [goalFormData, setGoalFormData] = useState({ periodStart: '', periodEnd: '', coachGoal: '', athleteGoal: '' });
  const [editingGoalId, setEditingGoalId] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // If user has athleteId, fetch athlete data
        if (user?.athleteId) {
          const athleteData = await athleteAPI.getById(user.athleteId);
          setAthlete(athleteData);
          
          // Fetch training sessions for this athlete - load all for Trophy Cabinet calculation
          const sessionsResponse = await trainingSessionAPI.getByAthlete(user.athleteId, undefined, undefined, true);
          // Handle response - when all=true, returns array directly, otherwise paginated object
          const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse.sessions || []);
          setSessions(sessionsData);

          // Fetch Banana Cup competitions for this athlete
          let athleteBananaCups: any[] = [];
          try {
            const allBananaCups = await bananaCupAPI.getAll();
            athleteBananaCups = allBananaCups.filter((cup: any) => 
              cup.athleteIds && cup.athleteIds.some((aid: any) => 
                (typeof aid === 'object' ? aid._id : aid) === user.athleteId
              )
            );
            setBananaCups(athleteBananaCups);
          } catch (bananaCupError) {
            console.warn('Failed to fetch Banana Cup data:', bananaCupError);
            setBananaCups([]);
          }

          // Calculate statistics - matching AthleteDetail.tsx logic
          const now = new Date();
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          const startOfMonthStr = startOfMonth.toISOString().split('T')[0];

          let totalJumps = 0;
          let totalDistance = 0;
          let distanceCount = 0;
          let bestDistance = 0;
          let totalJumpsThisMonth = 0;
          let totalDistanceThisMonth = 0;
          let distanceCountThisMonth = 0;
          let bestDistanceThisMonth = 0;
          let trainingSessionsThisMonth = 0;
          let competitionSessionsThisMonth = 0;

          // Process training sessions (ski_jumping type)
          sessionsData.forEach((session: TrainingSession) => {
            const isThisMonth = session.date >= startOfMonthStr;
            if (isThisMonth) {
              if (session.trainingType === 'competition') competitionSessionsThisMonth++;
              else trainingSessionsThisMonth++;
            }
            if (session.trainingType === 'ski_jumping' && session.jumps) {
              session.jumps.forEach((jump: { 
                athleteId?: { _id: string }; 
                distance?: number;
              }) => {
                if (jump.athleteId?._id === user.athleteId) {
                  totalJumps++;
                  if (isThisMonth) totalJumpsThisMonth++;
                  if (jump.distance) {
                    totalDistance += jump.distance;
                    distanceCount++;
                    if (jump.distance > bestDistance) bestDistance = jump.distance;
                    if (isThisMonth) {
                      totalDistanceThisMonth += jump.distance;
                      distanceCountThisMonth++;
                      if (jump.distance > bestDistanceThisMonth) bestDistanceThisMonth = jump.distance;
                    }
                  }
                }
              });
            } else if (session.trainingType === 'competition' && session.competitionJumps) {
              session.competitionJumps.forEach((compJump: { 
                athleteId?: { _id: string }; 
                trialRoundDistance?: number;
                firstRoundDistance?: number;
                secondRoundDistance?: number;
              }) => {
                if (compJump.athleteId?._id === user.athleteId) {
                  if (compJump.trialRoundDistance) {
                    totalDistance += compJump.trialRoundDistance;
                    distanceCount++;
                    if (compJump.trialRoundDistance > bestDistance) bestDistance = compJump.trialRoundDistance;
                    if (isThisMonth) {
                      totalDistanceThisMonth += compJump.trialRoundDistance;
                      distanceCountThisMonth++;
                      if (compJump.trialRoundDistance > bestDistanceThisMonth) bestDistanceThisMonth = compJump.trialRoundDistance;
                    }
                  }
                  if (compJump.firstRoundDistance) {
                    totalDistance += compJump.firstRoundDistance;
                    distanceCount++;
                    totalJumps++;
                    if (compJump.firstRoundDistance > bestDistance) bestDistance = compJump.firstRoundDistance;
                    if (isThisMonth) {
                      totalDistanceThisMonth += compJump.firstRoundDistance;
                      distanceCountThisMonth++;
                      totalJumpsThisMonth++;
                      if (compJump.firstRoundDistance > bestDistanceThisMonth) bestDistanceThisMonth = compJump.firstRoundDistance;
                    }
                  }
                  if (compJump.secondRoundDistance) {
                    totalDistance += compJump.secondRoundDistance;
                    distanceCount++;
                    totalJumps++;
                    if (compJump.secondRoundDistance > bestDistance) bestDistance = compJump.secondRoundDistance;
                    if (isThisMonth) {
                      totalDistanceThisMonth += compJump.secondRoundDistance;
                      distanceCountThisMonth++;
                      totalJumpsThisMonth++;
                      if (compJump.secondRoundDistance > bestDistanceThisMonth) bestDistanceThisMonth = compJump.secondRoundDistance;
                    }
                  }
                }
              });
            }
          });

          // Add Banana Cup competition jumps and count competitions
          let bananaCupCompetitionsCount = 0;
          athleteBananaCups.forEach((bananaCup: any) => {
            if (bananaCup.competitions && Array.isArray(bananaCup.competitions)) {
              bananaCup.competitions.forEach((competition: any) => {
                if (competition.isCompleted && competition.jumps) {
                  let hasAthleteJump = false;
                  competition.jumps.forEach((jump: any) => {
                    const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
                    if (jumpAthleteId === user.athleteId) {
                      hasAthleteJump = true;
                      // Count all rounds (can be up to 5 rounds)
                      let rounds = 0;
                      if (jump.firstRoundDistance) {
                        rounds++;
                        totalDistance += jump.firstRoundDistance;
                        distanceCount++;
                        if (jump.firstRoundDistance > bestDistance) {
                          bestDistance = jump.firstRoundDistance;
                        }
                      }
                      if (jump.secondRoundDistance) {
                        rounds++;
                        totalDistance += jump.secondRoundDistance;
                        distanceCount++;
                        if (jump.secondRoundDistance > bestDistance) {
                          bestDistance = jump.secondRoundDistance;
                        }
                      }
                      if (jump.thirdRoundDistance) {
                        rounds++;
                        totalDistance += jump.thirdRoundDistance;
                        distanceCount++;
                        if (jump.thirdRoundDistance > bestDistance) {
                          bestDistance = jump.thirdRoundDistance;
                        }
                      }
                      if (jump.fourthRoundDistance) {
                        rounds++;
                        totalDistance += jump.fourthRoundDistance;
                        distanceCount++;
                        if (jump.fourthRoundDistance > bestDistance) {
                          bestDistance = jump.fourthRoundDistance;
                        }
                      }
                      if (jump.fifthRoundDistance) {
                        rounds++;
                        totalDistance += jump.fifthRoundDistance;
                        distanceCount++;
                        if (jump.fifthRoundDistance > bestDistance) {
                          bestDistance = jump.fifthRoundDistance;
                        }
                      }
                      totalJumps += rounds;
                    }
                  });
                  if (hasAthleteJump) {
                    bananaCupCompetitionsCount++;
                  }
                }
              });
            }
          });

          // Count training sessions (exclude competition sessions)
          const trainingSessions = sessionsData.filter(
            (s: TrainingSession) => s.trainingType !== 'competition'
          );
          // Count competition sessions
          const competitionSessions = sessionsData.filter(
            (s: TrainingSession) => s.trainingType === 'competition'
          ).length;
          const totalSessions = trainingSessions.length + competitionSessions + bananaCupCompetitionsCount;

          const averageDistance = distanceCount > 0 ? totalDistance / distanceCount : 0;

          // Total wins = first-place finishes in competition sessions
          let totalWins = 0;
          let totalWinsThisMonth = 0;
          sessionsData.forEach((s: TrainingSession) => {
            const isThisMonth = s.date >= startOfMonthStr;
            if (s.trainingType === 'competition' && s.competitionJumps) {
              s.competitionJumps.forEach((cj: { athleteId?: { _id?: string } | string; result?: number }) => {
                const cjAid = typeof cj.athleteId === 'object' ? (cj.athleteId as any)?._id : cj.athleteId;
                if (String(cjAid) === user.athleteId && cj.result === 1) {
                  totalWins++;
                  if (isThisMonth) totalWinsThisMonth++;
                }
              });
            }
          });

          const totalSessionsThisMonth = trainingSessionsThisMonth + competitionSessionsThisMonth;
          const averageDistanceThisMonth = distanceCountThisMonth > 0
            ? Math.round((totalDistanceThisMonth / distanceCountThisMonth) * 10) / 10
            : 0;

          setStats({
            totalJumps,
            averageDistance: Math.round(averageDistance * 10) / 10,
            bestDistance,
            totalSessions,
            totalWins,
            totalJumpsThisMonth,
            averageDistanceThisMonth,
            bestDistanceThisMonth,
            totalSessionsThisMonth,
            totalWinsThisMonth,
          });
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [user?.athleteId]);

  const handleLinkAthlete = async () => {
    if (!athleteIdInput.trim()) {
      setLinkError('Please enter an athlete ID');
      return;
    }

    try {
      setLinkError('');
      setLinkSuccess('');
      await authAPI.linkAthlete(athleteIdInput.trim());
      setLinkSuccess('Athlete ID linked successfully! Refreshing...');
      // Reload the page to update user data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      setLinkError(error.message || 'Failed to link athlete ID');
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatTrainingType = (type: string) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const getEquipmentName = (equipmentId: any): string => {
    if (!equipmentId) return '-';
    if (typeof equipmentId === 'object' && equipmentId.name) {
      return equipmentId.name;
    }
    return 'Unknown';
  };

  const handleSaveSettings = async (newTopMetrics: string[]) => {
    try {
      await authAPI.updateProfile({
        dashboardPreferences: { topMetrics: newTopMetrics },
      });
      setTopMetrics(newTopMetrics);
      await refreshProfile();
    } catch (error) {
      console.error('Error saving dashboard settings:', error);
    }
  };

  const handleAddOption = (action: string) => {
    setAddFabExpanded(false);
    const plan = (athlete?.subscription?.plan || '').toLowerCase();
    const hasAnyPaidPlan = ['hs60', 'hs90', 'hs130', 'hs240'].includes(plan);
    const hasBananaCupAccess = ['hs90', 'hs130', 'hs240'].includes(plan);
    if (action === 'training') {
      navigate('/jumper/training-sessions', { state: { openNewSessionForm: true } });
      return;
    }
    if (action === 'planner' && hasBananaCupAccess) {
      navigate('/jumper/training-planner', { state: { openNewPlanForm: true } });
      return;
    }
    if (action === 'competition' && hasAnyPaidPlan) {
      navigate('/jumper/competition-sessions', { state: { openNewSessionForm: true } });
      return;
    }
    if (action === 'equipment' && hasAnyPaidPlan) {
      navigate('/jumper/equipment', { state: { openNewEquipmentForm: true } });
      return;
    }
    if (action === 'banana' && hasBananaCupAccess) {
      navigate('/jumper/banana-cup', { state: { openNewLeagueForm: true } });
      return;
    }
  };

  const getMetricDisplay = (key: string): { label: string; value: string; unit?: string; thisMonth: string; thisMonthUnit?: string } => {
    switch (key) {
      case 'totalJumps':
        return { label: 'Total Jumps', value: String(stats.totalJumps), thisMonth: String(stats.totalJumpsThisMonth) };
      case 'averageDistance':
        return {
          label: 'Average Distance',
          value: stats.averageDistance > 0 ? String(stats.averageDistance) : 'N/A',
          unit: stats.averageDistance > 0 ? 'm' : '',
          thisMonth: stats.averageDistanceThisMonth > 0 ? String(stats.averageDistanceThisMonth) : '0',
          thisMonthUnit: stats.averageDistanceThisMonth > 0 ? 'm' : '',
        };
      case 'bestDistance':
        return {
          label: 'Best Distance',
          value: stats.bestDistance > 0 ? String(stats.bestDistance) : 'N/A',
          unit: stats.bestDistance > 0 ? 'm' : '',
          thisMonth: stats.bestDistanceThisMonth > 0 ? String(stats.bestDistanceThisMonth) : '0',
          thisMonthUnit: stats.bestDistanceThisMonth > 0 ? 'm' : '',
        };
      case 'totalSessions':
        return { label: 'Training Sessions', value: String(stats.totalSessions), thisMonth: String(stats.totalSessionsThisMonth) };
      case 'totalWins':
        return { label: 'Total Wins', value: String(stats.totalWins), thisMonth: String(stats.totalWinsThisMonth) };
      default:
        return { label: key, value: '—', thisMonth: '—' };
    }
  };

  const toDateStr = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const getMonday = (d: Date) => {
    const m = new Date(d);
    const day = m.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    m.setDate(m.getDate() + diff);
    return m;
  };

  const distanceGraphData = useMemo(() => {
    if (!user?.athleteId || !sessions.length) return [];
    const aid = String(user.athleteId);
    const getJumpAthleteId = (j: { athleteId?: { _id?: string } | string }) => {
      const id = typeof j.athleteId === 'object' ? (j.athleteId as any)?._id : j.athleteId;
      return id != null ? String(id) : '';
    };
    const now = new Date();
    const rows: { week: string; distance: number }[] = [];
    for (let i = 9; i >= 0; i--) {
      const thisWeekMonday = getMonday(now);
      let weekStart: Date;
      let weekEnd: Date;
      if (i === 0) {
        weekStart = new Date(thisWeekMonday);
        weekEnd = new Date(now);
      } else {
        weekStart = new Date(thisWeekMonday);
        weekStart.setDate(weekStart.getDate() - 7 * i);
        weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
      }
      const weekStartStr = toDateStr(weekStart);
      const weekEndStr = toDateStr(weekEnd);
      let totalDist = 0;
      let count = 0;
      sessions.forEach((session: TrainingSession) => {
        const sessionDate = typeof session.date === 'string' ? session.date : (session as any).date?.split?.('T')?.[0];
        if (!sessionDate || sessionDate < weekStartStr || sessionDate > weekEndStr) return;
        if (session.jumps) {
          session.jumps.forEach((j: any) => {
            if (getJumpAthleteId(j) === aid && j.distance != null) {
              totalDist += Number(j.distance);
              count++;
            }
          });
        }
        if (session.competitionJumps) {
          session.competitionJumps.forEach((cj: any) => {
            if (getJumpAthleteId(cj) !== aid) return;
            [cj.trialRoundDistance, cj.firstRoundDistance, cj.secondRoundDistance].forEach((d: number | undefined) => {
              if (d != null) {
                totalDist += Number(d);
                count++;
              }
            });
          });
        }
      });
      const avg = count > 0 ? Math.round((totalDist / count) * 10) / 10 : 0;
      rows.push({
        week: i === 0 ? 'Now' : `W${10 - i}`,
        distance: avg,
      });
    }
    return rows;
  }, [user?.athleteId, sessions]);

  const weightGraphData = useMemo(() => {
    if (!athlete?.weightHistory?.length) return [];
    const now = new Date();
    const rows: { week: string; weight: number }[] = [];
    for (let i = 9; i >= 0; i--) {
      const thisWeekMonday = getMonday(now);
      let weekStart: Date;
      let weekEnd: Date;
      if (i === 0) {
        weekStart = new Date(thisWeekMonday);
        weekEnd = new Date(now);
      } else {
        weekStart = new Date(thisWeekMonday);
        weekStart.setDate(weekStart.getDate() - 7 * i);
        weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
      }
      const weekStartStr = toDateStr(weekStart);
      const weekEndStr = toDateStr(weekEnd);
      const inWeek = (athlete?.weightHistory || []).filter(
        (e: { date?: string }) => e.date && e.date >= weekStartStr && e.date <= weekEndStr
      );
      const latestInWeek = inWeek.length
        ? inWeek.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]
        : null;
      const w = latestInWeek ? (latestInWeek.weight ?? 0) : 0;
      rows.push({
        week: i === 0 ? 'Now' : `W${10 - i}`,
        weight: w,
      });
    }
    return rows;
  }, [athlete?.weightHistory]);

  const intensityGraphData = useMemo(() => {
    if (!user?.athleteId || !sessions.length) return [];
    const aid = String(user.athleteId);
    const now = new Date();
    const rows: { week: string; intensity: number }[] = [];
    for (let i = 9; i >= 0; i--) {
      const thisWeekMonday = getMonday(now);
      let weekStart: Date;
      let weekEnd: Date;
      if (i === 0) {
        weekStart = new Date(thisWeekMonday);
        weekEnd = new Date(now);
      } else {
        weekStart = new Date(thisWeekMonday);
        weekStart.setDate(weekStart.getDate() - 7 * i);
        weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
      }
      const weekStartStr = toDateStr(weekStart);
      const weekEndStr = toDateStr(weekEnd);
      let sum = 0;
      let count = 0;
      sessions.forEach((session: TrainingSession) => {
        const sessionDate = typeof session.date === 'string' ? session.date : (session as any).date?.split?.('T')?.[0];
        if (!sessionDate || sessionDate < weekStartStr || sessionDate > weekEndStr || !session.groundTrainings) return;
        session.groundTrainings.forEach((gt: any) => {
          const gtAid = typeof gt.athleteId === 'object' ? (gt.athleteId as any)?._id : gt.athleteId;
          if (String(gtAid) === aid && gt.intensity != null) {
            sum += Number(gt.intensity);
            count++;
          }
        });
      });
      const avg = count > 0 ? Math.round((sum / count) * 10) / 10 : 0;
      rows.push({
        week: i === 0 ? 'Now' : `W${10 - i}`,
        intensity: avg,
      });
    }
    return rows;
  }, [user?.athleteId, sessions]);

  const toggleSessionExpansion = (sessionId: string) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  // Weight, Height, Goals handlers
  const handleWeightSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.athleteId || !weightFormData.weight || !weightFormData.date) return;

    try {
      const weightValue = parseFloat(weightFormData.weight);
      const updatedAthlete = editingWeightEntryId
        ? await athleteAPI.updateWeight(user.athleteId, editingWeightEntryId, weightValue, weightFormData.date)
        : await athleteAPI.addWeight(user.athleteId, weightValue, weightFormData.date);
      setAthlete(updatedAthlete);
      setIsWeightModalOpen(false);
      setWeightFormData({ weight: '', date: '' });
      setEditingWeightEntryId(null);
    } catch (err) {
      console.error('Error updating weight:', err);
    }
  };

  const handleHeightSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.athleteId || !heightFormData.height || !heightFormData.date) return;

    try {
      const heightValue = parseFloat(heightFormData.height);
      const updatedAthlete = editingHeightEntryId
        ? await athleteAPI.updateHeight(user.athleteId, editingHeightEntryId, heightValue, heightFormData.date)
        : await athleteAPI.addHeight(user.athleteId, heightValue, heightFormData.date);
      setAthlete(updatedAthlete);
      setIsHeightModalOpen(false);
      setHeightFormData({ height: '', date: '' });
      setEditingHeightEntryId(null);
    } catch (err) {
      console.error('Error updating height:', err);
    }
  };

  const sortedWeightHistory = athlete?.weightHistory 
    ? [...athlete.weightHistory].sort((a, b) => {
        const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
        if (dateDiff !== 0) return dateDiff;
        const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bCreated - aCreated;
      })
    : [];

  const sortedHeightHistory = athlete?.heightHistory 
    ? [...athlete.heightHistory].sort((a, b) => {
        const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
        if (dateDiff !== 0) return dateDiff;
        const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bCreated - aCreated;
      })
    : [];

  const sortedGoals = athlete?.goals 
    ? [...athlete.goals].sort((a, b) => new Date(b.periodStart).getTime() - new Date(a.periodStart).getTime())
    : [];

  const getNextPeriodDates = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const handleGoalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.athleteId || !goalFormData.periodStart || !goalFormData.periodEnd) return;

    try {
      const goalData: any = {
        periodStart: goalFormData.periodStart,
        periodEnd: goalFormData.periodEnd
      };

      if (editingGoalId) {
        const existingGoal = athlete?.goals?.find(g => g._id === editingGoalId);
        if (existingGoal) {
          goalData.coachGoal = goalFormData.coachGoal || existingGoal.coachGoal;
          goalData.athleteGoal = goalFormData.athleteGoal || existingGoal.athleteGoal;
          goalData.coachGoalReached = existingGoal.coachGoalReached;
          goalData.athleteGoalReached = existingGoal.athleteGoalReached;
        }
      } else {
        goalData.coachGoal = goalFormData.coachGoal;
        goalData.athleteGoal = goalFormData.athleteGoal;
      }

      const updatedAthlete = await athleteAPI.updateGoal(user.athleteId, goalData);
      setAthlete(updatedAthlete);
      setIsNewGoalModalOpen(false);
      setGoalFormData({ periodStart: '', periodEnd: '', coachGoal: '', athleteGoal: '' });
      setEditingGoalId(null);
    } catch (err) {
      console.error('Error saving goal:', err);
    }
  };

  const openEditGoal = (goal: GoalPeriod) => {
    setGoalFormData({
      periodStart: goal.periodStart,
      periodEnd: goal.periodEnd,
      coachGoal: goal.coachGoal || '',
      athleteGoal: goal.athleteGoal || ''
    });
    setEditingGoalId(goal._id || null);
    setIsNewGoalModalOpen(true);
  };

  const handleToggleGoalReached = async (goalId: string, type: 'coach' | 'athlete', currentValue: boolean) => {
    if (!user?.athleteId) return;

    try {
      const goal = athlete?.goals?.find(g => g._id === goalId);
      if (!goal) return;

      const goalData: any = {
        periodStart: goal.periodStart,
        periodEnd: goal.periodEnd,
        coachGoal: goal.coachGoal,
        athleteGoal: goal.athleteGoal,
        coachGoalReached: type === 'coach' ? !currentValue : goal.coachGoalReached,
        athleteGoalReached: type === 'athlete' ? !currentValue : goal.athleteGoalReached
      };

      const updatedAthlete = await athleteAPI.updateGoal(user.athleteId, goalData);
      setAthlete(updatedAthlete);
    } catch (err) {
      console.error('Error toggling goal reached:', err);
    }
  };

  // Trophy Cabinet calculations
  const trophyCabinetData = calculateTrophyCabinet();

  function calculateTrophyCabinet() {
    if (!user?.athleteId || !sessions) {
      return { podiumByCategory: {}, top3Competitions: [] };
    }

    const categoryLabels: Record<string, string> = {
      'olympic_games': 'OLYMPIC GAMES',
      'world_championships': 'WORLD CHAMPIONSHIPS',
      'world_cup': 'WORLD CUP',
      'summer_grand_prix': 'SUMMER GRAND PRIX',
      'continental_cup': 'CONTINENTAL CUP',
      'fis_cup': 'FIS CUP',
      'alpen_cup': 'ALPEN CUP',
      'national_championships': 'NATIONAL CHAMPIONSHIPS',
      'national_cup': 'NATIONAL CUP',
      'national': 'NATIONAL CUP', // Backward compatibility
      'junior_world_championships': 'JUNIOR WORLD CHAMPIONSHIPS',
      'youth_olympic_games': 'YOUTH OLYMPIC GAMES',
      'eyof': 'EYOF',
      'opa_games': 'OPA GAMES',
    };

    // Only competition sessions (not Banana Cup)
    const competitionSessions = sessions.filter(session => 
      session.trainingType === 'competition' && session.competitionJumps
    );

    const podiumByCategory: Record<string, { first: number; second: number; third: number }> = {};
    const top3Competitions: Array<{
      date: string;
      category: string;
      location: string;
      hillSize?: string;
      position: number;
    }> = [];

    competitionSessions.forEach(session => {
      if (!session.category || !session.competitionJumps) return;

      const category = session.category === 'national' ? 'national_cup' : session.category;
      const categoryDisplay = categoryLabels[category] || category.toUpperCase();

      session.competitionJumps.forEach(jump => {
        const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
        const athleteId = String(athleteIdRaw);
        
        if (athleteId === user.athleteId && jump.result !== undefined && jump.result !== null) {
          const position = jump.result;
          
          // Track top 3 results
          if (position <= 3) {
            top3Competitions.push({
              date: session.date,
              category: categoryDisplay,
              location: session.location || '',
              hillSize: session.hillSize,
              position
            });

            // Track podium counts by category
            if (!podiumByCategory[categoryDisplay]) {
              podiumByCategory[categoryDisplay] = { first: 0, second: 0, third: 0 };
            }
            
            if (position === 1) {
              podiumByCategory[categoryDisplay].first++;
            } else if (position === 2) {
              podiumByCategory[categoryDisplay].second++;
            } else if (position === 3) {
              podiumByCategory[categoryDisplay].third++;
            }
          }
        }
      });
    });

    // Sort top 3 competitions by date (newest first)
    top3Competitions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return { podiumByCategory, top3Competitions };
  }

  const getSessionSummary = (session: TrainingSession): string => {
    if (!user?.athleteId) return '-';
    
    if (session.trainingType === 'ski_jumping' && session.jumps) {
      const athleteJumps = session.jumps.filter(j => j.athleteId?._id === user.athleteId);
      if (athleteJumps.length > 0) {
        const distances = athleteJumps.map(j => j.distance).filter(d => d !== undefined) as number[];
        if (distances.length > 0) {
          const maxDist = Math.max(...distances);
          const avgDist = distances.reduce((a, b) => a + b, 0) / distances.length;
          return `${athleteJumps.length} jump${athleteJumps.length > 1 ? 's' : ''} • Best: ${maxDist}m • Avg: ${Math.round(avgDist)}m`;
        }
        return `${athleteJumps.length} jump${athleteJumps.length > 1 ? 's' : ''}`;
      }
    } else if (session.trainingType === 'competition' && session.competitionJumps) {
      const athleteJumps = session.competitionJumps.filter(j => j.athleteId?._id === user.athleteId);
      if (athleteJumps.length > 0) {
        const jump = athleteJumps[0];
        const result = jump.result ? ` • Result: ${jump.result}` : '';
        if (jump.firstRoundDistance && jump.secondRoundDistance) {
          return `Round 1: ${jump.firstRoundDistance}m • Round 2: ${jump.secondRoundDistance}m${result}`;
        } else if (jump.firstRoundDistance) {
          return `Round 1: ${jump.firstRoundDistance}m${result}`;
        }
        return `Competition${result}`;
      }
    } else if (session.trainingType === 'ground_training' && session.groundTrainings) {
      const athleteTrainings = session.groundTrainings.filter((t: { athleteId: { _id: string; firstName: string; lastName: string } | string; trainingType: string; intensity: number }) => {
        const athleteId = typeof t.athleteId === 'object' ? t.athleteId._id : t.athleteId;
        return athleteId === user.athleteId;
      });
      if (athleteTrainings.length > 0) {
        const types = athleteTrainings.map((t: { trainingType: string }) => formatTrainingType(t.trainingType)).join(', ');
        return types;
      }
    } else if (session.trainingType === 'cross_country') {
      return `${session.startTime} - ${session.endTime}`;
    }
    return '-';
  };

  const getBananaCupCompetitionSummary = (competition: any): string => {
    if (!user?.athleteId || !competition.jumps || !Array.isArray(competition.jumps)) {
      return 'No jumps recorded';
    }

    const athleteJump = competition.jumps.find((jump: any) => {
      const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
      return jumpAthleteId === user.athleteId;
    });

    if (!athleteJump) {
      return 'No jumps recorded';
    }

    const parts: string[] = [];
    
    if (athleteJump.firstRoundDistance) {
      parts.push(`Round 1: ${athleteJump.firstRoundDistance}m`);
    }
    if (athleteJump.secondRoundDistance) {
      parts.push(`Round 2: ${athleteJump.secondRoundDistance}m`);
    }
    if (athleteJump.thirdRoundDistance) {
      parts.push(`Round 3: ${athleteJump.thirdRoundDistance}m`);
    }
    if (athleteJump.fourthRoundDistance) {
      parts.push(`Round 4: ${athleteJump.fourthRoundDistance}m`);
    }
    if (athleteJump.fifthRoundDistance) {
      parts.push(`Round 5: ${athleteJump.fifthRoundDistance}m`);
    }
    if (athleteJump.position) {
      parts.push(`Position: ${athleteJump.position}`);
    }
    if (athleteJump.points !== undefined && athleteJump.points !== null) {
      parts.push(`Points: ${athleteJump.points}`);
    }

    return parts.length > 0 ? parts.join(' • ') : 'No data';
  };

  const getAllSessions = (): SessionItem[] => {
    const sessionItems: SessionItem[] = [];

    // Add training sessions - only include sessions that have been started
    sessions.forEach(session => {
      const hasJumps = session.jumps && session.jumps.length > 0;
      const hasCompetitionJumps = session.competitionJumps && session.competitionJumps.length > 0;
      const hasGroundTrainings = session.groundTrainings && session.groundTrainings.length > 0;
      
      // Only include sessions that have been started
      if (hasJumps || hasCompetitionJumps || hasGroundTrainings) {
        sessionItems.push({
          _id: session._id,
          type: 'training',
          trainingType: session.trainingType,
          date: session.date,
          location: session.location,
          details: getSessionSummary(session)
        });
      }
    });

    // Add Banana Cup competitions
    bananaCups.forEach(bananaCup => {
      if (bananaCup.competitions && Array.isArray(bananaCup.competitions)) {
        bananaCup.competitions.forEach((competition: any) => {
          if (competition.isCompleted && competition.jumps && Array.isArray(competition.jumps)) {
            const athleteJump = competition.jumps.find((jump: any) => {
              const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
              return jumpAthleteId === user?.athleteId;
            });

            if (athleteJump) {
              sessionItems.push({
                _id: `${bananaCup._id}-${competition.competitionNumber}`,
                type: 'banana_cup',
                trainingType: 'banana_cup',
                date: competition.date || '',
                location: competition.location || '',
                details: getBananaCupCompetitionSummary(competition),
                bananaCupName: bananaCup.name,
                competitionNumber: competition.competitionNumber
              });
            }
          }
        });
      }
    });

    // Sort by date (most recent first)
    return sessionItems.sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!user?.athleteId) {
    return (
      <Container>
        <PageTitle>Welcome!</PageTitle>
        <WelcomeMsg>
          Welcome to <Sj>sj</Sj><Mate>Mate</Mate> - your personal ski jumping dashboard.
        </WelcomeMsg>
        <LinkAthleteCard>
          <LinkAthleteTitle>Link Your Athlete Account</LinkAthleteTitle>
          <p style={{ marginBottom: '1rem', color: 'var(--text-gray)', fontSize: '0.9rem' }}>
            To view your training data, please link your athlete ID. Ask your coach for your athlete ID if you don't have it.
          </p>
          {linkError && <ErrorMessage>{linkError}</ErrorMessage>}
          {linkSuccess && <SuccessMessage>{linkSuccess}</SuccessMessage>}
          <LinkAthleteInput
            type="text"
            placeholder="Enter your athlete ID"
            value={athleteIdInput}
            onChange={(e) => setAthleteIdInput(e.target.value)}
          />
          <LinkButton onClick={handleLinkAthlete}>Link Athlete ID</LinkButton>
        </LinkAthleteCard>
      </Container>
    );
  }

  if (!athlete) {
    return (
      <Container>
        <PageTitle>Welcome!</PageTitle>
        <WelcomeMsg>
          Welcome to <Sj>sj</Sj><Mate>Mate</Mate> - your personal ski jumping dashboard.
        </WelcomeMsg>
        <p>Loading athlete data...</p>
      </Container>
    );
  }

  const plan = (athlete?.subscription?.plan || '').toLowerCase();
  const hasAnyPaidPlan = ['hs60', 'hs90', 'hs130', 'hs240'].includes(plan);
  const hasBananaCupAccess = ['hs90', 'hs130', 'hs240'].includes(plan);

  return (
    <Container>
      <SettingsWrapper>
        <HeaderLeft>
          <TitleAndBadgeRow>
            <PageTitle>Home Dashboard</PageTitle>
            <PlanBadgeWrapper>
              <PlanBadge plan={athlete?.subscription?.plan || 'free'} size="medium" />
            </PlanBadgeWrapper>
          </TitleAndBadgeRow>
          <WelcomeMsg>
            Welcome to <Sj>sj</Sj><Mate>Mate</Mate>. Here&apos;s an overview of your performance.
          </WelcomeMsg>
        </HeaderLeft>
        <TopRightGroup>
          <AddFabContainer ref={addFabContainerRef}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
              <AddFabButton
                onClick={() => setAddFabExpanded(prev => !prev)}
                title={addFabExpanded ? 'Close menu' : 'Add new'}
                aria-label={addFabExpanded ? 'Close menu' : 'Add new'}
              >
                +
              </AddFabButton>
              <AddLabel>ADD</AddLabel>
            </div>
            {addFabExpanded && (
              <AddFabMenu>
                <AddFabOption onClick={() => handleAddOption('training')}>Add new training session</AddFabOption>
                {hasAnyPaidPlan && (
                  <>
                    <AddFabOption onClick={() => handleAddOption('competition')}>Add new competition session</AddFabOption>
                    <AddFabOption onClick={() => handleAddOption('equipment')}>Add new equipment</AddFabOption>
                  </>
                )}
                {hasBananaCupAccess && (
                  <>
                    <AddFabOption onClick={() => handleAddOption('planner')}>Plan new training session</AddFabOption>
                    <AddFabOption onClick={() => handleAddOption('banana')}>Create new Banana Cup</AddFabOption>
                  </>
                )}
              </AddFabMenu>
            )}
          </AddFabContainer>
          <MetricsButtonColumn>
            <SettingsButton
              onClick={() => setShowSettings(true)}
              title="Dashboard Settings"
              aria-label="Dashboard Settings"
            >
              <Icon component={AiFillSetting} />
            </SettingsButton>
            <ChooseMetricsLabel>{'CHOOSE\nMETRICS'}</ChooseMetricsLabel>
          </MetricsButtonColumn>
          <HelpButtonColumn>
            <HelpButtonAsLink to="/how-to-start#jumper-home" title="How to start" aria-label="How to start">
              ?
            </HelpButtonAsLink>
            <HelpLabel>HELP</HelpLabel>
          </HelpButtonColumn>
        </TopRightGroup>
      </SettingsWrapper>

      <JumperDashboardSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        topMetrics={topMetrics}
        onSave={handleSaveSettings}
      />

      {/* Distance graph - above metrics */}
      <GraphSection>
        <GraphTitle>Average distance per week (last 10 weeks)</GraphTitle>
        <GraphContainer>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={distanceGraphData} margin={{ top: 8, right: 8, left: 0, bottom: isMobile ? 40 : 4 }}>
              <XAxis dataKey="week" tick={{ fontSize: 11 }} axisLine={{ stroke: 'rgba(0,0,0,0.12)' }} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', fontSize: '0.8rem' }}
                formatter={(value: number) => [value ? `${value} m` : '—', 'Avg distance']}
                labelFormatter={(label: string) => `Week ${label}`}
              />
              <Line
                type="monotone"
                dataKey="distance"
                name="Avg distance"
                stroke="var(--accent-orange)"
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </GraphContainer>
      </GraphSection>

      {/* Metrics - from settings (up to 4) */}
      <MetricsGrid>
        {topMetrics.slice(0, 4).map((metricKey) => {
          const metric = getMetricDisplay(metricKey);
          return (
            <MetricCard key={metricKey}>
              <MetricLabel>{metric.label}</MetricLabel>
              <MetricValue>{metric.value}{metric.unit || ''}</MetricValue>
              <MetricDescription>{metric.thisMonth}{metric.thisMonthUnit || ''} this month</MetricDescription>
            </MetricCard>
          );
        })}
      </MetricsGrid>

      {/* Weight, Height, Goals, Trophy Cabinet Buttons */}
      <ActionButtonContainer>
        {/* Weight Button */}
        <ActionButtonWrapper>
          <ActionButton 
            onClick={() => setOpenSection(openSection === 'weight' ? null : 'weight')}
            isExpanded={openSection === 'weight'}
          >
            WEIGHT
          </ActionButton>
        </ActionButtonWrapper>

        {/* Height Button */}
        <ActionButtonWrapper>
          <ActionButton 
            onClick={() => setOpenSection(openSection === 'height' ? null : 'height')}
            isExpanded={openSection === 'height'}
          >
            HEIGHT
          </ActionButton>
        </ActionButtonWrapper>

        {/* Goals Button */}
        <ActionButtonWrapper>
          <ActionButton 
            onClick={() => setOpenSection(openSection === 'goals' ? null : 'goals')}
            isExpanded={openSection === 'goals'}
          >
            GOALS
          </ActionButton>
        </ActionButtonWrapper>

        {/* Trophy Cabinet Button */}
        <ActionButtonWrapper>
          <ActionButton 
            onClick={() => setOpenSection(openSection === 'trophy' ? null : 'trophy')}
            isExpanded={openSection === 'trophy'}
          >
            TROPHY CABINET
          </ActionButton>
        </ActionButtonWrapper>
      </ActionButtonContainer>

      {/* Dropdowns Container - Each dropdown appears in its own full-width space */}
      <DropdownsContainer>
        {/* Weight Dropdown */}
        <AnimatedDropdownContainer isOpen={openSection === 'weight'}>
          <SectionTitle>Weight</SectionTitle>
          <div style={{ marginBottom: '1rem' }}>
            <ActionButton 
              onClick={() => {
                setEditingWeightEntryId(null);
                setWeightFormData({ weight: '', date: '' });
                setIsWeightModalOpen(true);
              }}
              showArrow={false}
              style={{ fontSize: '0.65rem', padding: '0.4rem 0.8rem' }}
            >
              UPDATE WEIGHT
            </ActionButton>
          </div>
          {sortedWeightHistory.length > 0 && (
            <WeightHistoryTable>
              <thead>
                <WeightHistoryRow>
                  <WeightHistoryHeader>Date</WeightHistoryHeader>
                  <WeightHistoryHeader>Weight (kg)</WeightHistoryHeader>
                </WeightHistoryRow>
              </thead>
              <tbody>
                {sortedWeightHistory.map((entry: any, index) => (
                  <WeightHistoryRow key={entry._id || index}>
                    <WeightHistoryCell>{formatDate(entry.date)}</WeightHistoryCell>
                    <WeightHistoryCell>{entry.weight} kg</WeightHistoryCell>
                    <WeightHistoryCell style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: 'var(--accent-orange)',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          marginRight: '0.5rem',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setEditingWeightEntryId(entry._id);
                          setWeightFormData({ weight: String(entry.weight ?? ''), date: entry.date || '' });
                          setIsWeightModalOpen(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#cc0000',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                        onClick={async () => {
                          if (!user?.athleteId || !entry._id) return;
                          const confirmed = window.confirm('Delete this weight entry?');
                          if (!confirmed) return;
                          try {
                            const updated = await athleteAPI.deleteWeight(user.athleteId, entry._id);
                            setAthlete(updated);
                          } catch (err) {
                            console.error('Error deleting weight entry:', err);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </WeightHistoryCell>
                  </WeightHistoryRow>
                ))}
              </tbody>
            </WeightHistoryTable>
          )}
        </AnimatedDropdownContainer>

        {/* Height Dropdown */}
        <AnimatedDropdownContainer isOpen={openSection === 'height'}>
          <SectionTitle>Height</SectionTitle>
          <div style={{ marginBottom: '1rem' }}>
            <ActionButton 
              onClick={() => {
                setEditingHeightEntryId(null);
                setHeightFormData({ height: '', date: '' });
                setIsHeightModalOpen(true);
              }}
              showArrow={false}
              style={{ fontSize: '0.65rem', padding: '0.4rem 0.8rem' }}
            >
              UPDATE HEIGHT
            </ActionButton>
          </div>
          {sortedHeightHistory.length > 0 && (
            <HeightHistoryTable>
              <thead>
                <HeightHistoryRow>
                  <HeightHistoryHeader>Date</HeightHistoryHeader>
                  <HeightHistoryHeader>Height (cm)</HeightHistoryHeader>
                </HeightHistoryRow>
              </thead>
              <tbody>
                {sortedHeightHistory.map((entry: any, index) => (
                  <HeightHistoryRow key={entry._id || index}>
                    <HeightHistoryCell>{formatDate(entry.date)}</HeightHistoryCell>
                    <HeightHistoryCell>{entry.height} cm</HeightHistoryCell>
                    <HeightHistoryCell style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: 'var(--accent-orange)',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          marginRight: '0.5rem',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setEditingHeightEntryId(entry._id);
                          setHeightFormData({ height: String(entry.height ?? ''), date: entry.date || '' });
                          setIsHeightModalOpen(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#cc0000',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                        onClick={async () => {
                          if (!user?.athleteId || !entry._id) return;
                          const confirmed = window.confirm('Delete this height entry?');
                          if (!confirmed) return;
                          try {
                            const updated = await athleteAPI.deleteHeight(user.athleteId, entry._id);
                            setAthlete(updated);
                          } catch (err) {
                            console.error('Error deleting height entry:', err);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </HeightHistoryCell>
                  </HeightHistoryRow>
                ))}
              </tbody>
            </HeightHistoryTable>
          )}
        </AnimatedDropdownContainer>

        {/* Goals Dropdown */}
        <AnimatedDropdownContainer isOpen={openSection === 'goals'}>
          <SectionTitle>Goals & Targets</SectionTitle>
          <div style={{ marginBottom: '1rem' }}>
            <ActionButton 
              onClick={() => {
                const dates = getNextPeriodDates();
                setGoalFormData({ periodStart: dates.start, periodEnd: dates.end, coachGoal: '', athleteGoal: '' });
                setEditingGoalId(null);
                setIsNewGoalModalOpen(true);
              }}
              showArrow={false}
              style={{ fontSize: '0.75rem', padding: '0.5rem 1rem' }}
            >
              + New Goal Period
            </ActionButton>
          </div>
          
          {sortedGoals.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {sortedGoals.map((goal) => {
                const isPeriodEnded = new Date(goal.periodEnd) < new Date();
                return (
                  <div 
                    key={goal._id} 
                    style={{ 
                      background: 'var(--bg-sidebar)', 
                      padding: '1.5rem', 
                      borderRadius: 'var(--radius-md)',
                      boxShadow: 'var(--shadow-sm)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-gray)', marginBottom: '0.3rem' }}>
                          PERIOD: {formatDate(goal.periodStart)} - {formatDate(goal.periodEnd)}
                        </div>
                        {isPeriodEnded && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--accent-orange)', fontWeight: 600 }}>
                            Period Ended
                          </div>
                        )}
                      </div>
                      <ActionButton 
                        onClick={() => openEditGoal(goal)}
                        showArrow={false}
                        style={{ fontSize: '0.65rem', padding: '0.4rem 0.8rem' }}
                      >
                        Edit
                      </ActionButton>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                      <div>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-gray)', marginBottom: '0.5rem' }}>
                          COACH GOAL:
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', marginBottom: '0.5rem', minHeight: '2rem' }}>
                          {goal.coachGoal || <span style={{ fontStyle: 'italic', opacity: 0.6 }}>No goal set</span>}
                        </div>
                        {isPeriodEnded && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={goal.coachGoalReached}
                              onChange={() => handleToggleGoalReached(goal._id!, 'coach', goal.coachGoalReached)}
                              style={{ cursor: 'pointer' }}
                            />
                            <label style={{ fontSize: '0.75rem', cursor: 'pointer' }}>Goal Reached</label>
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-gray)', marginBottom: '0.5rem' }}>
                          ATHLETE GOAL:
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', marginBottom: '0.5rem', minHeight: '2rem' }}>
                          {goal.athleteGoal || <span style={{ fontStyle: 'italic', opacity: 0.6 }}>No goal set</span>}
                        </div>
                        {isPeriodEnded && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={goal.athleteGoalReached}
                              onChange={() => handleToggleGoalReached(goal._id!, 'athlete', goal.athleteGoalReached)}
                              style={{ cursor: 'pointer' }}
                            />
                            <label style={{ fontSize: '0.75rem', cursor: 'pointer' }}>Goal Reached</label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-gray)', fontStyle: 'italic' }}>
              No goals set yet. Create a new goal period to get started.
            </div>
          )}
        </AnimatedDropdownContainer>

        {/* Trophy Cabinet Dropdown */}
        <AnimatedDropdownContainer isOpen={openSection === 'trophy'}>
          <SectionTitle>Trophy Cabinet</SectionTitle>
          
          {/* Podium Counts by Category */}
          {Object.keys(trophyCabinetData.podiumByCategory).length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-gray)', marginBottom: '1rem' }}>
                NUMBER OF ACHIEVED PODIUMS FOR EACH CATEGORY
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {Object.entries(trophyCabinetData.podiumByCategory).map(([category, counts]) => (
                  <div 
                    key={category}
                    style={{ 
                      background: 'var(--bg-sidebar)', 
                      padding: '1rem', 
                      borderRadius: 'var(--radius-md)',
                      boxShadow: 'var(--shadow-sm)'
                    }}
                  >
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--accent-orange)', marginBottom: '0.5rem' }}>
                      {category}
                    </div>
                    <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.75rem', color: 'var(--text-gray)' }}>
                      <span>🥇 1st: {counts.first}</span>
                      <span>🥈 2nd: {counts.second}</span>
                      <span>🥉 3rd: {counts.third}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top 3 Competitions List */}
          {trophyCabinetData.top3Competitions.length > 0 && (
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-gray)', marginBottom: '1rem' }}>
                ALL RESULTS WITH ACHIEVED PODIUM
              </div>
              <TrainingTable>
                <thead>
                  <TableRow>
                    <TableHeader>Date</TableHeader>
                    <TableHeader>Category</TableHeader>
                    <TableHeader>Location</TableHeader>
                    <TableHeader>Hill Size</TableHeader>
                    <TableHeader>Position</TableHeader>
                  </TableRow>
                </thead>
                <tbody>
                  {trophyCabinetData.top3Competitions.map((comp, index) => (
                    <TableRow key={index}>
                      <TableCell>{formatDate(comp.date)}</TableCell>
                      <TableCell>{comp.category}</TableCell>
                      <TableCell>{comp.location}</TableCell>
                      <TableCell>{comp.hillSize || '-'}</TableCell>
                      <TableCell>
                        {comp.position === 1 ? '🥇 1st' : comp.position === 2 ? '🥈 2nd' : '🥉 3rd'}
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </TrainingTable>
            </div>
          )}

          {Object.keys(trophyCabinetData.podiumByCategory).length === 0 && trophyCabinetData.top3Competitions.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-gray)', fontStyle: 'italic' }}>
              No podium achievements yet. Keep training and competing!
            </div>
          )}
        </AnimatedDropdownContainer>
      </DropdownsContainer>

      {/* Two graphs: Weight + Ground training intensity */}
      <TwoGraphsRow>
        <SmallGraphSection>
          <GraphTitle>Weight (last 10 weeks)</GraphTitle>
          <SmallGraphContainer>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weightGraphData} margin={{ top: 8, right: 8, left: 0, bottom: isMobile ? 40 : 4 }}>
                <XAxis dataKey="week" tick={{ fontSize: 10 }} axisLine={{ stroke: 'rgba(0,0,0,0.12)' }} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', fontSize: '0.8rem' }} formatter={(v: number) => [v ? `${v} kg` : '—', 'Weight']} labelFormatter={(l: string) => `Week ${l}`} />
                <Line type="monotone" dataKey="weight" name="Weight" stroke="var(--accent-orange)" strokeWidth={2} dot={{ r: 2 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </SmallGraphContainer>
        </SmallGraphSection>
        <SmallGraphSection>
          <GraphTitle>Average ground training intensity (last 10 weeks)</GraphTitle>
          <SmallGraphContainer>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={intensityGraphData} margin={{ top: 8, right: 8, left: 0, bottom: isMobile ? 40 : 4 }}>
                <XAxis dataKey="week" tick={{ fontSize: 10 }} axisLine={{ stroke: 'rgba(0,0,0,0.12)' }} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', fontSize: '0.8rem' }} formatter={(v: number) => [v != null ? `${v}` : '—', 'Intensity']} labelFormatter={(l: string) => `Week ${l}`} />
                <Line type="monotone" dataKey="intensity" name="Intensity" stroke="var(--accent-orange)" strokeWidth={2} dot={{ r: 2 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </SmallGraphContainer>
        </SmallGraphSection>
      </TwoGraphsRow>


      {/* Weight Modal */}
      {isWeightModalOpen && (
        <ModalOverlay onClick={() => setIsWeightModalOpen(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <h2>Update Weight</h2>
            <Form onSubmit={handleWeightSubmit}>
              <FormGroup>
                <Label htmlFor="weight">Weight (kg)</Label>
                <Input
                  type="number"
                  id="weight"
                  name="weight"
                  value={weightFormData.weight}
                  onChange={(e) => setWeightFormData({ ...weightFormData, weight: e.target.value })}
                  placeholder="Enter weight"
                  step="0.1"
                  min="0"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="date">Date</Label>
                <Input
                  type="date"
                  id="date"
                  name="date"
                  value={weightFormData.date}
                  onChange={(e) => setWeightFormData({ ...weightFormData, date: e.target.value })}
                  required
                />
              </FormGroup>
              <ButtonGroup>
                <ModalButton type="button" onClick={() => setIsWeightModalOpen(false)}>
                  Cancel
                </ModalButton>
                <ModalButton type="submit" variant="primary">
                  Add Weight
                </ModalButton>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Height Modal */}
      {isHeightModalOpen && (
        <ModalOverlay onClick={() => setIsHeightModalOpen(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <h2>Update Height</h2>
            <Form onSubmit={handleHeightSubmit}>
              <FormGroup>
                <Label htmlFor="height">Height (cm)</Label>
                <Input
                  type="number"
                  id="height"
                  name="height"
                  value={heightFormData.height}
                  onChange={(e) => setHeightFormData({ ...heightFormData, height: e.target.value })}
                  placeholder="Enter height"
                  step="0.1"
                  min="0"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="heightDate">Date</Label>
                <Input
                  type="date"
                  id="heightDate"
                  name="heightDate"
                  value={heightFormData.date}
                  onChange={(e) => setHeightFormData({ ...heightFormData, date: e.target.value })}
                  required
                />
              </FormGroup>
              <ButtonGroup>
                <ModalButton type="button" onClick={() => setIsHeightModalOpen(false)}>
                  Cancel
                </ModalButton>
                <ModalButton type="submit" variant="primary">
                  Add Height
                </ModalButton>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Goal Modal */}
      {isNewGoalModalOpen && (
        <ModalOverlay onClick={() => {
          setIsNewGoalModalOpen(false);
          setGoalFormData({ periodStart: '', periodEnd: '', coachGoal: '', athleteGoal: '' });
          setEditingGoalId(null);
        }}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <h2>{editingGoalId ? 'Edit Goal Period' : 'New Goal Period'}</h2>
            <Form onSubmit={handleGoalSubmit}>
              <FormGroup>
                <Label htmlFor="periodStart">Period Start Date</Label>
                <Input
                  type="date"
                  id="periodStart"
                  name="periodStart"
                  value={goalFormData.periodStart}
                  onChange={(e) => setGoalFormData({ ...goalFormData, periodStart: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="periodEnd">Period End Date</Label>
                <Input
                  type="date"
                  id="periodEnd"
                  name="periodEnd"
                  value={goalFormData.periodEnd}
                  onChange={(e) => setGoalFormData({ ...goalFormData, periodEnd: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="coachGoal">Coach Goal</Label>
                <Input
                  type="text"
                  id="coachGoal"
                  name="coachGoal"
                  value={goalFormData.coachGoal}
                  onChange={(e) => setGoalFormData({ ...goalFormData, coachGoal: e.target.value })}
                  placeholder="Enter coach's goal for this period"
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="athleteGoal">Athlete Goal</Label>
                <Input
                  type="text"
                  id="athleteGoal"
                  name="athleteGoal"
                  value={goalFormData.athleteGoal}
                  onChange={(e) => setGoalFormData({ ...goalFormData, athleteGoal: e.target.value })}
                  placeholder="Enter athlete's goal for this period"
                />
              </FormGroup>
              <ButtonGroup>
                <ModalButton 
                  type="button" 
                  onClick={() => {
                    setIsNewGoalModalOpen(false);
                    setGoalFormData({ periodStart: '', periodEnd: '', coachGoal: '', athleteGoal: '' });
                    setEditingGoalId(null);
                  }}
                >
                  Cancel
                </ModalButton>
                <ModalButton type="submit" variant="primary">
                  {editingGoalId ? 'Update Goal' : 'Create Goal'}
                </ModalButton>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default JumperHome;

